// Per-payable invoiced totals, folded client-side from the contract's invoice
// list. Display-only convenience (like qtyLeft) — the backend stays canonical
// for every committed figure; this exists so the select-charges catalogue can
// grey a fully-invoiced milestone and hint at partial/pending draws without a
// server round trip. `approved` mirrors computeLineBalances' definition of
// consumption (approved invoices only); `inReview` mirrors contention (review
// invoices only). Draft and queried invoices count nowhere. A charge's amount
// is the sum of its splits' amounts; `excludeInvoiceId` keeps edit mode from
// counting the invoice being edited against itself.
import type { Invoice, InvoiceCharge } from "../../../types/finance";

/** Catalogue handle for the payable a charge covers, or undefined for a
 *  free-form charge with no coverage reference. Shared with the invoice form
 *  state, which uses the same handles to re-open stored charges. */
export function payableIdFromCharge(c: InvoiceCharge): string | undefined {
  if (c.milestoneId) return `milestone:${c.milestoneId}`;
  if (c.rateItemId) return `rate:${c.rateItemId}`;
  if (c.sowId) return `sow:${c.sowId}`;
  return undefined;
}

export interface PayableInvoicedTotals {
  approved: number;
  inReview: number;
}

export function invoicedByPayable(
  invoices: Invoice[],
  opts: { excludeInvoiceId?: string } = {},
): Map<string, PayableInvoicedTotals> {
  const totals = new Map<string, PayableInvoicedTotals>();
  for (const inv of invoices) {
    if (inv.id === opts.excludeInvoiceId) continue;
    if (inv.status !== "approved" && inv.status !== "review") continue;
    for (const charge of inv.charges) {
      const payableId = payableIdFromCharge(charge);
      if (!payableId) continue;
      const amount = charge.splits.reduce((sum, s) => sum + s.amount, 0);
      const entry = totals.get(payableId) ?? { approved: 0, inReview: 0 };
      if (inv.status === "approved") entry.approved += amount;
      else entry.inReview += amount;
      totals.set(payableId, entry);
    }
  }
  return totals;
}
